import { Line } from 'recharts';
import TimeSeriesLineChart from '@/components/charts/TimeSeriesLineChart';
import { formatCurrency } from '@/utils/currencyUtils';
import { createXAxisDateFormatter } from '@/utils';
import type { CampaignTypesApiResponse, VisibleCategories } from '@/types';
import CampaignTypeEarningsTooltipContent from './CampaignTypeEarningsTooltipContent';

interface CampaignTypeEarningsChartProps {
  data: CampaignTypesApiResponse;
  currency: string;
  visibleCategories: VisibleCategories;
}

/**
 * This component is used to display a line chart containing earnings per campaign category type over time.
 * @param data - the data returned from the campaign-types api endpoint.
 * @param currency - the network currency
 * @param visibleCategories - the visible categories for the campaign types modal.
 * @returns The chart component.
 */
const CampaignTypeEarningsChart = ({
  data,
  currency,
  visibleCategories,
}: CampaignTypeEarningsChartProps) => {
  const formatXAxisDate = createXAxisDateFormatter();

  return (
    <TimeSeriesLineChart<CampaignTypesApiResponse[number]>
      data={data}
      dateKey="calculation_date"
      xAxisTickFormatter={formatXAxisDate}
      yAxisFormatter={(value) =>
        value !== 0 ? String(formatCurrency(value, currency, true)) : ''
      }
      ariaLabel="Earnings per campaign category type over time"
      tooltipContent={(payload) => (
        <CampaignTypeEarningsTooltipContent
          payload={payload}
          visibleCategories={visibleCategories}
        />
      )}
      minHeight={400}
      maxHeight={700}
    >
      {data.length > 0 &&
        Object.entries(data[0]?.totals ?? {}).map(([key, value], i) => (
          <Line
            key={`${i}-${key}`}
            dot={false}
            animationDuration={300}
            name={value.category}
            strokeWidth={2}
            type="linear"
            dataKey={`totals['${key}'].total.amount`}
            stroke={visibleCategories[key as keyof typeof visibleCategories].color}
            fillOpacity={1}
            fill="url(#colorUv)"
            hide={!visibleCategories[key as keyof typeof visibleCategories].visible}
          />
        ))}
    </TimeSeriesLineChart>
  );
};

export default CampaignTypeEarningsChart;
